import { useState, useRef } from 'react';
import WithDataWrapper from '../../reusable/WithDataWrapper';
import { getStates, getDemographicsCategories, createEOA } from '../../../services/eoaConfigServices';
import SelectDropdown from '../../reusable/SelectDropdown';
import { TextField, Chip } from '@mui/material';
import CheckboxGroup from '../CheckBoxGroup';
import CustomButton from '../../reusable/CustomButton';
import useErrorModal from '../../hooks/useErrorModal';
import useUserDetails from '../../hooks/useUserDetails';
import { useNavigate } from 'react-router-dom';
import type { State } from '../../../services/eoaConfigServices';
import { DemographicCategoryOption } from '../../../interfaces/EOAInterface';
import { AxiosError } from 'axios';

import './CreateEOA.css';

interface CreateEOAProps {
  states: State[] | null;
  demographics: DemographicCategoryOption[] | null;
}
function CreateEOA({ states, demographics }: CreateEOAProps) {
  const [selectedState, setSelectedState] = useState('');
  const [districtName, setDistrictName] = useState('');
  const [districtZipCode, setDistrictZipCode] = useState('');
  const [graduatingYear, setGraduatingYear] = useState(0);
  const [academicPeriodsNames, setAcademicPeriodsNames] = useState({
    academic_period_1: '',
    academic_period_2: '',
    academic_period_3: '',
    academic_period_4: '',
  });
  const [academicPeriodsAliases, setAcademicPeriodsAliases] = useState({
    academic_period_alias_1: [] as string[],
    academic_period_alias_2: [] as string[],
    academic_period_alias_3: [] as string[],
    academic_period_alias_4: [] as string[],
  });
  const [satTestTypeIncluded, setSatTestTypeIncluded] = useState(false);
  const [actTestTypeIncluded, setActTestTypeIncluded] = useState(false);

  const [demographicsCheckboxes, setDemographicsCheckboxes] = useState(
    demographics?.map((demographicCategory) => {
      return {
        name: demographicCategory.name,
        value: false,
        label: demographicCategory.display_name,
      };
    }) || []
  );

  const academicPeriodAlias1 = useRef<null | HTMLInputElement>(null);
  const academicPeriodAlias2 = useRef<null | HTMLInputElement>(null);
  const academicPeriodAlias3 = useRef<null | HTMLInputElement>(null);
  const academicPeriodAlias4 = useRef<null | HTMLInputElement>(null);

  const {
    userDetails: { data: userDetails },
  } = useUserDetails();

  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  const navigate = useNavigate();

  if (!states) return <p>States are unavailable!</p>;
  if (!demographics) return <p>Demographics categories are unavailable!</p>;

  const updateAcademicPeriodName = (event: React.ChangeEvent<HTMLInputElement>) => {
    let academicPeriod = event.target.name;
    let newName = event.target.value;
    setAcademicPeriodsNames({ ...academicPeriodsNames, [academicPeriod]: newName });
  };

  const updateAcademicPeriodAlias = (target: HTMLInputElement | null, academicPeriod: string) => {
    if (!target) return;

    let newName = target.value;
    const currentAliases = academicPeriodsAliases[academicPeriod as keyof typeof academicPeriodsAliases];
    setAcademicPeriodsAliases({ ...academicPeriodsAliases, [academicPeriod]: [...currentAliases, newName] });

    target.value = '';
  };

  const handleDeleteAlias = (aliasToRemove: string, academicPeriod: string) => {
    const currentAliases = academicPeriodsAliases[academicPeriod as keyof typeof academicPeriodsAliases].filter(
      (alias) => alias !== aliasToRemove
    );
    setAcademicPeriodsAliases({ ...academicPeriodsAliases, [academicPeriod]: [...currentAliases] });
  };

  const checkBoxes = [
    { name: 'SAT', value: satTestTypeIncluded, label: 'SAT' },
    { name: 'ACT', value: actTestTypeIncluded, label: 'ACT' },
    ...demographicsCheckboxes,
  ];

  const handleCheckboxChange = (name: string, checked: boolean) => {
    switch (name) {
      case 'SAT':
        setSatTestTypeIncluded(checked);
        break;
      case 'ACT':
        setActTestTypeIncluded(checked);
        break;
      default:
        let demographicsObject = demographicsCheckboxes.find((demographic) => demographic.name === name);
        if (!demographicsObject) break;

        demographicsObject.value = checked;
        setDemographicsCheckboxes(JSON.parse(JSON.stringify(demographicsCheckboxes)));
        break;
    }
  };

  const handleCreateEOAA = async () => {
    if (!userDetails) {
      handleOpenErrorModal('Missing user data!', new Error('Missing user data!'));
      return;
    }

    try {
      const mergedTestTypes = [];
      if (satTestTypeIncluded) mergedTestTypes.push('SAT');
      if (actTestTypeIncluded) mergedTestTypes.push('ACT');

      const demographics: string[] = [];
      demographicsCheckboxes.forEach((demographicObject) => {
        if (demographicObject.value) demographics.push(demographicObject.name);
      });

      const requestBody = {
        district_name: districtName,
        graduating_year: graduatingYear,
        demographics,
        academic_period_names: [
          academicPeriodsNames.academic_period_1,
          academicPeriodsNames.academic_period_2,
          academicPeriodsNames.academic_period_3,
          academicPeriodsNames.academic_period_4,
        ],
        academic_period_aliases: {
          [academicPeriodsNames.academic_period_1]: academicPeriodsAliases.academic_period_alias_1,
          [academicPeriodsNames.academic_period_2]: academicPeriodsAliases.academic_period_alias_2,
          [academicPeriodsNames.academic_period_3]: academicPeriodsAliases.academic_period_alias_3,
          [academicPeriodsNames.academic_period_4]: academicPeriodsAliases.academic_period_alias_4,
        },
        test_types: mergedTestTypes,
      };
      await createEOA(userDetails.access_token, requestBody);
      navigate('/eoa');
    } catch (error) {
      handleOpenErrorModal('Error occurred while creating EOAA!', error as AxiosError);
    }
  };

  return (
    <div className="EOAA-config-data">
      <h3>Create EOAA</h3>
      <SelectDropdown
        label="State Name"
        selectedValue={selectedState}
        handleChange={(event) => setSelectedState(event.target.value)}
        items={states.map((state) => ({
          label: state.name,
          value: state.state_id,
        }))}
        formControlVariant="outlined"
      />
      <TextField
        required
        label="District Name"
        variant="outlined"
        value={districtName}
        onChange={(event) => setDistrictName(event.target.value)}
      />
      <TextField
        required
        label="District zip code"
        variant="outlined"
        value={districtZipCode}
        onChange={(event) => setDistrictZipCode(event.target.value)}
      />
      <TextField
        required
        label="Graduating year"
        variant="outlined"
        value={graduatingYear}
        onChange={(event) => setGraduatingYear(Number(event.target.value))}
        type="number"
        inputProps={{ min: 0, pattern: '^[1-9]d*$' }}
      />
      <TextField
        required
        label="Academic period 1 name"
        variant="outlined"
        value={academicPeriodsNames.academic_period_1}
        onChange={updateAcademicPeriodName}
        name="academic_period_1"
      />
      <TextField
        required
        label="Academic period 1 alias"
        variant="outlined"
        inputRef={academicPeriodAlias1}
        name="academic_period_alias_1"
      />
      <div className="aliases-container">
        {academicPeriodsAliases.academic_period_alias_1.map((alias) => (
          <Chip
            key={alias}
            label={alias}
            variant="outlined"
            onDelete={() => handleDeleteAlias(alias, 'academic_period_alias_1')}
          />
        ))}
      </div>
      <CustomButton
        variant="primary"
        buttonText="Add alias"
        buttonAction={() => updateAcademicPeriodAlias(academicPeriodAlias1.current, 'academic_period_alias_1')}
      />
      <TextField
        required
        label="Academic period 2 name"
        variant="outlined"
        value={academicPeriodsNames.academic_period_2}
        onChange={updateAcademicPeriodName}
        name="academic_period_2"
      />
      <TextField
        required
        label="Academic period 2 alias"
        variant="outlined"
        inputRef={academicPeriodAlias2}
        name="academic_period_alias_2"
      />
      <div className="aliases-container">
        {academicPeriodsAliases.academic_period_alias_2.map((alias) => (
          <Chip
            key={alias}
            label={alias}
            variant="outlined"
            onDelete={() => handleDeleteAlias(alias, 'academic_period_alias_2')}
          />
        ))}
      </div>
      <CustomButton
        variant="primary"
        buttonText="Add alias"
        buttonAction={() => updateAcademicPeriodAlias(academicPeriodAlias2.current, 'academic_period_alias_2')}
      />
      <TextField
        required
        label="Academic period 3 name"
        variant="outlined"
        value={academicPeriodsNames.academic_period_3}
        onChange={updateAcademicPeriodName}
        name="academic_period_3"
      />
      <TextField
        required
        label="Academic period 3 alias"
        variant="outlined"
        inputRef={academicPeriodAlias3}
        name="academic_period_alias_3"
      />
      <div className="aliases-container">
        {academicPeriodsAliases.academic_period_alias_3.map((alias) => (
          <Chip
            key={alias}
            label={alias}
            variant="outlined"
            onDelete={() => handleDeleteAlias(alias, 'academic_period_alias_3')}
          />
        ))}
      </div>
      <CustomButton
        variant="primary"
        buttonText="Add alias"
        buttonAction={() => updateAcademicPeriodAlias(academicPeriodAlias3.current, 'academic_period_alias_3')}
      />
      <TextField
        required
        label="Academic period 4 name"
        variant="outlined"
        value={academicPeriodsNames.academic_period_4}
        onChange={updateAcademicPeriodName}
        name="academic_period_4"
      />
      <TextField
        required
        label="Academic period 4 alias"
        variant="outlined"
        inputRef={academicPeriodAlias4}
        name="academic_period_alias_4"
      />
      <div className="aliases-container">
        {academicPeriodsAliases.academic_period_alias_4.map((alias) => (
          <Chip
            key={alias}
            label={alias}
            variant="outlined"
            onDelete={() => handleDeleteAlias(alias, 'academic_period_alias_4')}
          />
        ))}
      </div>
      <CustomButton
        variant="primary"
        buttonText="Add alias"
        buttonAction={() => updateAcademicPeriodAlias(academicPeriodAlias4.current, 'academic_period_alias_4')}
      />
      <CheckboxGroup checkboxes={checkBoxes} onChange={handleCheckboxChange} />
      <CustomButton variant="primary" size="medium" buttonText="Create EOAA" buttonAction={handleCreateEOAA} />
    </div>
  );
}

interface EOAData {
  states: State[] | null;
  demographics: DemographicCategoryOption[] | null;
}

export default function CreateEOAWithData() {
  const fetchData = async (eoaId: string, access_token: string) => {
    const statesPromise = getStates(eoaId, access_token);
    const demographicsPromise = getDemographicsCategories(eoaId, access_token);

    let result = await Promise.allSettled([statesPromise, demographicsPromise]);

    const data: EOAData = {
      states: null,
      demographics: null,
    };

    if (result[0].status === 'fulfilled') {
      data.states = result[0].value.data;
    }

    if (result[1].status === 'fulfilled') {
      data.demographics = result[1].value.data;
    }

    return { data };
  };

  return (
    <WithDataWrapper
      fetchData={fetchData}
      errorMessage="Error occurred while fetching EOA settings data!"
      initialState={{
        states: null,
        demographics: null,
      }}
    >
      {(data) => <CreateEOA states={data.states} demographics={data.demographics} />}
    </WithDataWrapper>
  );
}
